import { cn } from '@heidi/utils';
import { isEmpty } from 'lodash-es';
import * as React from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  accessoryStart?: React.ReactNode;
  accessoryEnd?: React.ReactNode;
  wrapperClassName?: string;
  onInputBlur?: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  maxWidth?: string;
  testId?: string;
  fullWidth?: boolean;
}

const InlineInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({
    accessoryStart,
    accessoryEnd,
    className,
    wrapperClassName,
    onInputBlur,
    type,
    value,
    placeholder,
    inputRef: inputRefProp,
    maxWidth = '300px',
    testId,
    fullWidth = false,
    ...props
  }) => {
    const defaultInputRef = useRef<HTMLInputElement>(null);
    const inputRef = inputRefProp ?? defaultInputRef;
    const measureDivRef = useRef<HTMLDivElement>(null);
    const [inputWidth, setInputWidth] = useState<string>('auto');
    const latestValueRef = useRef<string>((value as string) || '');
    const latestWidthRef = useRef<number>(0);
    const isUpdatingRef = useRef<boolean>(false);

    const calculateWidth = useCallback(() => {
      if (isUpdatingRef.current || !measureDivRef.current) return;

      isUpdatingRef.current = true;

      try {
        measureDivRef.current.textContent = isEmpty(latestValueRef.current)
          ? placeholder || ''
          : latestValueRef.current;

        const measuredWidth = measureDivRef.current.offsetWidth + 2;
        const maxWidthValue = parseInt(maxWidth, 10) || 300;
        const newWidth = Math.min(measuredWidth, maxWidthValue);

        if (Math.abs(newWidth - latestWidthRef.current) > 1) {
          latestWidthRef.current = newWidth;
          setInputWidth(`${newWidth}px`);
        }
      } finally {
        isUpdatingRef.current = false;
      }
    }, [maxWidth, placeholder]);

    useLayoutEffect(() => {
      latestValueRef.current = (value as string) || '';
      calculateWidth();
    }, [value, placeholder, calculateWidth]);

    useLayoutEffect(() => {
      const initialMeasurement = () => {
        calculateWidth();
        void document.fonts.ready.then(calculateWidth);
      };

      initialMeasurement();

      const handleResize = () => {
        calculateWidth();
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [calculateWidth]);

    return (
      <div
        data-testid={testId}
        className={cn(
          'inline-flex items-center gap-2 border-b-2 border-dashed border-border',
          wrapperClassName
        )}
      >
        {accessoryStart && <div>{accessoryStart}</div>}
        <div
          style={{
            position: 'relative',
            width: fullWidth ? '100%' : inputWidth,
            minWidth: '20px',
            maxWidth: fullWidth ? 'none' : maxWidth,
          }}
        >
          <input
            type={type}
            value={value}
            className={cn(
              'w-full truncate border-none bg-transparent text-3xl font-medium leading-tight tracking-tight outline-none transition-colors file:bg-transparent file:text-sm file:font-medium placeholder:text-text-secondary focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              className
            )}
            placeholder={placeholder}
            onBlur={(e) => {
              if (onInputBlur) onInputBlur();
              if (props.onBlur) props.onBlur(e);
            }}
            ref={inputRef}
            {...props}
          />

          {/* Hidden div to measure text width */}
          <div
            ref={measureDivRef}
            aria-hidden="true"
            style={{
              position: 'absolute',
              visibility: 'hidden',
              height: 0,
              whiteSpace: 'pre',
              overflow: 'hidden',
              padding: 0,
              border: 0,
            }}
            className={cn('text-3xl font-medium leading-tight tracking-tight', className)}
          />
        </div>
        {accessoryEnd && (
          <div onClick={() => inputRef.current?.focus()} className="cursor-pointer">
            {accessoryEnd}
          </div>
        )}
      </div>
    );
  }
);
InlineInput.displayName = 'InlineInput';

export { InlineInput };
